import React, {
  createContext,
  useContext,
  useMemo,
  type ReactNode,
} from "react";
import { useSortable } from "@dnd-kit/sortable";
import type {
  DraggableAttributes,
  DraggableSyntheticListeners,
  UniqueIdentifier,
} from "@dnd-kit/core";
import { CSS } from "@dnd-kit/utilities";
import "./SortableItem.scss";

interface SortableItemContextValue {
  attributes: Partial<DraggableAttributes>;
  listeners: DraggableSyntheticListeners;
  ref: (element: HTMLElement | null) => void;
}

const SortableItemContext = createContext<SortableItemContextValue>({
  attributes: {},
  listeners: undefined,
  ref() {},
});

interface SortableItemProps {
  children: ReactNode;
  id: UniqueIdentifier;
}

const SortableItem: React.FC<SortableItemProps> = ({ children, id }) => {
  const {
    attributes,
    isDragging,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
  } = useSortable({ id });
  const context = useMemo(
    () => ({
      attributes,
      listeners,
      ref: setActivatorNodeRef,
    }),
    [attributes, listeners, setActivatorNodeRef]
  );
  const style: React.CSSProperties = {
    opacity: isDragging ? 0.85 : undefined,
    transform: CSS.Translate.toString(transform),
    transition,
  };

  return (
    <SortableItemContext.Provider value={context}>
      <li className="sortable-item" ref={setNodeRef} style={style}>
        {children}
      </li>
    </SortableItemContext.Provider>
  );
};

export function DragHandle() {
  const { attributes, listeners, ref } = useContext(SortableItemContext);

  return (
    <div
      className={"sortable-drag-handle"}
      {...attributes}
      {...listeners}
      ref={ref}
    >
      <i className={"seem icon seem-icon-drag"} />
    </div>
  );
}

export default SortableItem;
